import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/data/heroSlides";

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback((next: number) => {
    setActive((next + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % heroSlides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [paused]);

  const slide = heroSlides[active];

  return (
    <section
      className="relative w-full overflow-hidden bg-background-50"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative h-[560px] w-full sm:h-[600px] md:h-[680px]">
        {heroSlides.map((item, index) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-[900ms] ${
              index === active ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden={index !== active}
          >
            <img
              src={item.image}
              alt={`${item.title}${item.highlight} 影片主视觉`}
              title="色戒无删减版 高清观影主视觉"
              className={`h-full w-full object-cover object-top ${index === active ? "animate-slow-zoom" : ""}`}
            />
          </div>
        ))}

        <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/80 to-background-50/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-50/60" />

        <div className="container-page relative flex h-full flex-col justify-center pb-16 pt-24">
          <div key={slide.id} className="max-w-2xl">
            <div className="animate-fade-up flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full border border-accent-500/40 bg-accent-500/10 px-3 py-1 font-label text-[11px] tracking-[0.2em] text-accent-400">
                <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
                {slide.eyebrow}
              </span>
              <span className="font-label text-[11px] tracking-[0.2em] text-foreground-600">2007 · 剧情 / 谍战</span>
            </div>

            <h1 className="animate-fade-up delay-1 mt-6 font-heading text-4xl leading-tight text-foreground-950 sm:text-5xl md:text-6xl">
              {slide.title}
              <span className="ml-2 text-accent-500">{slide.highlight}</span>
            </h1>

            <p className="animate-fade-up delay-2 mt-5 max-w-xl text-sm leading-relaxed text-foreground-700 md:text-base">
              {slide.description}
            </p>

            <div className="animate-fade-up delay-3 mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to={`/detail/${slide.chapterId}`}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-play-fill text-base leading-none" />
                在线观看本章
              </Link>
              <Link
                to="/category"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-950/25 px-6 py-3 font-label text-sm text-foreground-900 transition-colors hover:border-accent-500 hover:text-accent-400"
              >
                <i className="ri-list-check text-base leading-none" />
                浏览全部章节
              </Link>
            </div>

            <div className="animate-fade-up delay-4 mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-film-line text-sm leading-none text-accent-500" />
                共 12 个剧情章节
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-magic-line text-sm leading-none text-accent-500" />
                完整剧情 · 逐章解读
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-shield-check-line text-sm leading-none text-accent-500" />
                播放区域为演示模块
              </span>
            </div>
          </div>
        </div>

        <div className="container-page absolute inset-x-0 bottom-6 flex items-end justify-between">
          <div className="flex items-center gap-2">
            {heroSlides.map((item, index) => (
              <button
                key={item.id}
                type="button"
                aria-label={`切换到第 ${index + 1} 张主视觉`}
                onClick={() => go(index)}
                className={`h-1.5 cursor-pointer rounded-full transition-all duration-300 ${
                  index === active ? "w-10 bg-accent-500" : "w-4 bg-foreground-950/30 hover:bg-foreground-950/60"
                }`}
              />
            ))}
            <span className="ml-2 font-label text-[11px] tracking-[0.2em] text-foreground-600">
              0{active + 1} / 0{heroSlides.length}
            </span>
          </div>

          <div className="hidden items-center gap-2 sm:flex">
            <button
              type="button"
              aria-label="上一张"
              onClick={() => go(active - 1)}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-foreground-950/20 text-foreground-800 transition-colors hover:border-accent-500 hover:text-accent-400"
            >
              <i className="ri-arrow-left-s-line text-lg leading-none" />
            </button>
            <button
              type="button"
              aria-label="下一张"
              onClick={() => go(active + 1)}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-foreground-950/20 text-foreground-800 transition-colors hover:border-accent-500 hover:text-accent-400"
            >
              <i className="ri-arrow-right-s-line text-lg leading-none" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}